<script setup lang="ts">
import {
  differenceInDays,
  format,
  getMonth,
  getYear,
  isBefore,
  isSameMonth,
} from "date-fns";

const props = defineProps<{
  id: string;
  slug: string;
  name: string;

  universityId: string;
  universityName: string;
  universitySlug: string;
  location: string;
  duration: string;
  ranking: string;

  tuitionFees: string;
  tuitionFeeCurrency: string;
  recurringPaymentType: string;
  admissionFees: string;
  admissionFeeCurrency: string;
  intakes: Array<{
    start_date: string;
    end_date: string;
  }>;
  // deadline: string;

  programHighlight: string;
  studyFormat: string;
  degree: string;
  language: string;
  schedulePreference: string;
}>();

const nearestIntakeIndex = ref(-1);

const getNearestStartDate = (intakeDates: any) => {
  const now = new Date();

  if (nearestIntakeIndex.value !== -1 && intakeDates?.length) {
    const startDate = new Date(
      intakeDates[nearestIntakeIndex.value].start_date,
    );
    const endDate = new Date(getNearestEndDate(intakeDates));
    const currentYear = now.getFullYear();

    if (isBefore(startDate, endDate)) {
      startDate.setFullYear(currentYear + 1);
    } else {
      startDate.setFullYear(currentYear);
    }

    // Additional check if date has already passed
    if (startDate < now) {
      startDate.setFullYear(currentYear + 1);
      endDate.setFullYear(currentYear + 1);
    }

    return startDate;
  }

  return new Date();
};

const getNearestEndDate = (intakeDates: any) => {
  const now = new Date();

  // Find index of nearest end date that's in the future
  const nearestIndex = intakeDates?.findIndex((intake) => {
    const endDate = new Date(intake.end_date);
    // Set year to current year to only compare month and day
    endDate.setFullYear(now.getFullYear());
    // If date has passed this year, check next year
    if (endDate < now) {
      endDate.setFullYear(now.getFullYear() + 1);
    }
    return endDate > now;
  });

  if (nearestIndex !== -1) {
    nearestIntakeIndex.value = nearestIndex;
  }

  if (nearestIndex === -1 || !intakeDates?.length) {
    return new Date();
  }

  const endDate = new Date(intakeDates[nearestIndex].end_date);
  endDate.setFullYear(now.getFullYear());
  if (endDate < now) {
    endDate.setFullYear(now.getFullYear() + 1);
  }

  return endDate;
};

function formatDate(inputDate, currentDate = new Date()) {
  // if (isSameMonth(inputDate, currentDate)) {
  //   const daysLeft = differenceInDays(
  //     new Date(inputDate.setHours(0, 0, 0, 0)),
  //     new Date(currentDate.setHours(0, 0, 0, 0)),
  //   );

  //   if (daysLeft < 0) {
  //     return format(inputDate, "dd MMM yyyy");
  //   }

  //   return `${daysLeft} day(s) left`;
  // } else if (isBefore(inputDate, currentDate)) {
  //   return "Deadline over";
  // }
  if (isBefore(inputDate, currentDate)) {
    return "Deadline over";
  }

  return format(inputDate, "MMM yyyy");
}

const formatTuitionFee = (tuitionFeeStr?: string | number) => {
  if (tuitionFeeStr === undefined) return "Data Not Available";
  const tuitionFee = Number(tuitionFeeStr);
  if (tuitionFee === 0) return "Free";
  if (tuitionFee === -1) return "Data Not Available";

  return `${numberFormatter.format(tuitionFee)}`;
};

const formatPaymentType = (
  paymentType?: string,
  tuitionFeeStr?: string | number,
) => {
  const tuitionFee = Number(tuitionFeeStr);
  if (tuitionFee === 0 || tuitionFee === -1 || tuitionFeeStr === undefined)
    return "";

  return " / " + paymentType?.toLocaleLowerCase();
};

const getCurrencyIcon = (currency?: string) => {
  switch (currency?.toUpperCase()) {
    case "THB":
      return "<span>฿</span>";
    case "USD":
      return "<span>$</span>";
    case "EURO":
      return "<span>€</span>";
    case "POUND":
      return "<span>£</span>";
  }
};
</script>

<template>
  <!-- desktop card -->
  <div
    class="hidden md:block rounded-lg bg-white border border-slate-200 hover:drop-shadow"
  >
    <div class="grid grid-cols-10 border-b border-slate-200">
      <div class="col-span-8 p-6">
        <div class="space-y-2">
          <NuxtLink
            :to="`/programs/${slug}?start_date=${intakes?.at(0)?.start_date}&end_date=${intakes?.at(0)?.end_date}&from=explore`"
          >
            <h3
              class="text-2xl font-bold text-primary-500 hover:text-primary-700"
            >
              {{ name }}
            </h3>
          </NuxtLink>

          <div class="flex items-center gap-1 text-slate-800 text-sm">
            <NuxtLink
              :to="`/universities/${universitySlug}?&from=explore`"
              class="text-blue-600 hover:underline"
              >{{ universityName }}</NuxtLink
            >

            <Divider
              v-if="location"
              type="solid"
              layout="vertical"
              class="before:w-px before:bg-slate-200"
            />
            <p v-if="location">{{ location }}</p>

            <Divider
              type="solid"
              layout="vertical"
              class="before:w-px before:bg-slate-200"
            />

            <p>{{ ranking }}</p>
          </div>

          <div class="py-4 flex gap-3">
            <BadgeComp
              v-if="programHighlight !== 'N/A'"
              :text="programHighlight"
              icon="pi-star-fill"
              active
            />
            <BadgeComp :text="studyFormat" icon="pi-graduation-cap" />
            <BadgeComp :text="degree" icon="pi-book" />
            <BadgeComp :text="language" icon="pi-comment" />
            <BadgeComp :text="schedulePreference" icon="pi-clock" />
          </div>

          <div class="flex flex-nowrap items-start">
            <div>
              <p class="text-sm text-nowrap text-slate-600 font-extralight">
                Upcoming intake
              </p>
              <p class="font-bold text-primary-500 text-lg">
                {{
                  intakes?.length > 0
                    ? formatDate(getNearestStartDate(intakes))
                    : "Data not available"
                }}
              </p>
            </div>

            <Divider
              type="solid"
              layout="vertical"
              class="before:w-px h-8 before:h-8 before:bg-slate-200"
            />

            <div>
              <p class="text-sm text-slate-600 font-extralight">
                Total duration
              </p>
              <p class="font-bold text-primary-500 text-lg">
                {{ duration || "Data not available" }}
              </p>
            </div>
            <Divider
              type="solid"
              layout="vertical"
              class="before:w-px h-8 before:h-8 before:bg-slate-200"
            />
            <div>
              <p class="text-sm text-slate-600 font-extralight">
                Admission fees
              </p>
              <p
                class="font-bold text-primary-500 flex gap-1 items-center text-lg"
              >
                <span v-if="Number(admissionFees || 0) > 0">
                  <span v-html="getCurrencyIcon(admissionFeeCurrency)"></span>
                  {{ numberFormatter.format(Number(admissionFees || 0)) }}
                </span>
                <span v-else>Free</span>
              </p>
            </div>
            <Divider
              type="solid"
              layout="vertical"
              class="before:w-px h-8 before:h-8 before:bg-slate-200"
            />
            <div>
              <p class="text-sm text-slate-600 font-extralight">Tuition fees</p>
              <p
                class="font-bold text-primary-500 flex gap-1 items-center text-lg"
              >
                <span>
                  <span v-html="getCurrencyIcon(tuitionFeeCurrency)"></span>
                  {{ formatTuitionFee(tuitionFees) }}</span
                >
                <span class="text-sm">
                  {{ formatPaymentType(recurringPaymentType, tuitionFees) }}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-span-2 border-l border-slate-200 p-6">
        <div class="flex h-full">
          <div class="m-auto text-center space-y-1">
            <i class="pi pi-calendar text-3xl"></i>
            <p class="text-sm text-slate-600">Application deadline</p>
            <p class="text-red-500 font-bold text-lg">
              {{
                intakes?.length > 0
                  ? formatDate(getNearestEndDate(intakes))
                  : "Data no available"
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="p-6 bg-white flex justify-between rounded-lg">
      <CompareBtn :id="id" :slug="slug" />

      <div class="flex gap-6">
        <WishlistBtn :id="id" />

        <NuxtLink
          :to="`/programs/${slug}?start_date=${intakes?.at(0)?.start_date}&end_date=${intakes?.at(0)?.end_date}&from=explore`"
        >
          <Button
            icon="pi pi-angle-right"
            icon-pos="right"
            label="Read more"
            class="bg-[#1C477B] !text-sm text-white py-2 px-4"
          />
        </NuxtLink>
      </div>
    </div>
  </div>

  <!-- Mobile Card -->
  <div
    class="rounded-lg bg-white border border-slate-200 hover:drop-shadow block md:hidden"
  >
    <div class="p-4 space-y-4">
      <div>
        <div class="flex items-center justify-between mb-2.5">
          <NuxtLink
            :to="`/programs/${slug}?start_date=${intakes?.at(0)?.start_date}&end_date=${intakes?.at(0)?.end_date}&from=explore`"
          >
            <h3 class="text-lg font-bold text-primary-500 leading-snug">
              {{ name }}
            </h3>
          </NuxtLink>
          <WishlistBtn :id="id" />
        </div>

        <div class="text-sm text-slate-800 flex flex-wrap items-center gap-1">
          <NuxtLink
            :to="`/universities/${universitySlug}?&from=explore`"
            class="text-blue-600 hover:underline"
            >{{ universityName }}</NuxtLink
          >
          <span v-if="location">• {{ location }}</span>
          <span v-if="ranking !== 'EduRank Ranking 0'">• {{ ranking }}</span>
          <span v-else>Ranking Not Available</span>
        </div>
      </div>

      <div class="flex flex-wrap gap-2">
        <BadgeComp
          v-if="programHighlight !== 'N/A'"
          :text="programHighlight"
          icon="pi-star-fill"
          active
        />
        <BadgeComp :text="studyFormat" icon="pi-graduation-cap" />
        <BadgeComp :text="degree" icon="pi-book" />
        <BadgeComp :text="language" icon="pi-comment" />
        <BadgeComp :text="schedulePreference" icon="pi-clock" />
      </div>

      <div class="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p class="text-slate-600">Upcoming intake</p>
          <p class="text-primary-500 font-semibold">
            {{
              intakes?.length > 0
                ? formatDate(getNearestStartDate(intakes))
                : "Data not available"
            }}
          </p>
        </div>
        <div>
          <p class="text-slate-600">Total Duration</p>
          <p class="text-primary-500 font-semibold">{{ duration }}</p>
        </div>
        <div>
          <p class="text-slate-600">Admission fees</p>
          <p class="text-primary-500 font-semibold">
            <span v-if="Number(admissionFees || 0) > 0">
              <span v-html="getCurrencyIcon(admissionFeeCurrency)"></span>
              {{ numberFormatter.format(Number(admissionFees || 0)) }}</span
            >
            <span v-else>Free</span>
          </p>
        </div>
        <div>
          <p class="text-slate-600">Tuition fees</p>
          <p class="text-primary-500 font-semibold">
            <span v-html="getCurrencyIcon(tuitionFeeCurrency)"></span>
            {{ formatTuitionFee(tuitionFees) }}
            <span class="text-xs">
              {{ formatPaymentType(recurringPaymentType, tuitionFees) }}</span
            >
          </p>
        </div>
      </div>

      <div class="flex items-center gap-3 mt-2">
        <i class="pi pi-calendar text-lg"></i>
        <p class="text-sm text-slate-600">Application deadline:</p>
        <p class="text-red-500 font-semibold">
          {{
            intakes?.length > 0
              ? formatDate(getNearestEndDate(intakes))
              : "Data no available"
          }}
        </p>
      </div>
    </div>

    <div
      class="p-4 flex lg:flex-col gap-3 sm:flex-row sm:justify-between sm:items-center border-t border-slate-200"
    >
      <CompareBtn :id="id" :slug="slug" />
      <NuxtLink
        :to="`/programs/${slug}?start_date=${intakes?.at(0)?.start_date}&end_date=${intakes?.at(0)?.end_date}&from=explore`"
      >
        <Button
          icon="pi pi-angle-right"
          icon-pos="right"
          label="Read more"
          class="bg-[#1C477B] w-full text-white !text-sm px-4 py-2"
        />
      </NuxtLink>
    </div>
  </div>
</template>
